/**
 * Componente MagneticButton
 * Botón con efecto magnético que sigue el cursor
 * Inspirado en micro-interacciones de sitios Awwwards
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';

/**
 * Botón con efecto magnético
 * El botón se mueve sutilmente hacia el cursor cuando está cerca
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido del botón
 * @param {Function} props.onClick - Manejador de click
 * @param {string} props.className - Clases CSS adicionales
 * @param {number} props.strength - Fuerza del efecto magnético (0-1)
 * @param {string} props.variant - Variante de estilo ('primary', 'secondary', 'outline', 'ghost')
 * @param {string} props.size - Tamaño del botón ('sm', 'md', 'lg', 'xl')
 * @param {boolean} props.disabled - Si el botón está deshabilitado
 * @param {string} props.type - Tipo de botón HTML
 * @returns {JSX.Element} Botón magnético
 */
const MagneticButton = ({
  children,
  onClick,
  className = '',
  strength = 0.4,
  variant = 'primary',
  size = 'md',
  disabled = false,
  type = 'button',
}) => {
  const buttonRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const button = buttonRef.current;
    const text = textRef.current;

    if (!button || disabled) return;

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      // Mover el botón hacia el cursor
      gsap.to(button, {
        x: x * strength,
        y: y * strength,
        duration: 0.4,
        ease: 'power2.out',
      });

      // El texto se mueve un poco más para dar sensación de profundidad
      if (text) {
        gsap.to(text, {
          x: x * strength * 0.5,
          y: y * strength * 0.5,
          duration: 0.4,
          ease: 'power2.out',
        });
      }
    };

    const handleMouseLeave = () => {
      // Volver a la posición original con efecto elástico
      gsap.to(button, {
        x: 0,
        y: 0,
        duration: 0.7,
        ease: 'elastic.out(1, 0.3)',
      });

      if (text) {
        gsap.to(text, {
          x: 0,
          y: 0,
          duration: 0.7,
          ease: 'elastic.out(1, 0.3)',
        });
      }
    };

    const handleMouseEnter = () => {
      // Pequeña escala al entrar
      gsap.to(button, {
        scale: 1.05,
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseUp = () => {
      gsap.to(button, {
        scale: 1.05,
        duration: 0.2,
        ease: 'power2.out',
      });
    };

    const handleMouseDown = () => {
      gsap.to(button, {
        scale: 0.95,
        duration: 0.1,
        ease: 'power2.out',
      });
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('mouseleave', handleMouseLeave);
    button.addEventListener('mouseenter', handleMouseEnter);
    button.addEventListener('mousedown', handleMouseDown);
    button.addEventListener('mouseup', handleMouseUp);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('mouseleave', handleMouseLeave);
      button.removeEventListener('mouseenter', handleMouseEnter);
      button.removeEventListener('mousedown', handleMouseDown);
      button.removeEventListener('mouseup', handleMouseUp);
    };
  }, [strength, disabled]);

  // Estilos base
  const baseStyles = `
    relative inline-flex items-center justify-center
    font-lato font-semibold
    rounded-full
    transition-colors duration-300
    will-change-transform
    focus:outline-none focus:ring-2 focus:ring-offset-2
    overflow-hidden
  `;

  // Variantes de estilo
  const variantStyles = {
    primary: `
      bg-intercasa-red text-white
      hover:bg-red-800
      focus:ring-intercasa-red
      disabled:bg-gray-400
    `,
    secondary: `
      bg-intercasa-yellow text-intercasa-dark
      hover:bg-yellow-400
      focus:ring-intercasa-yellow
      disabled:bg-gray-300
    `,
    outline: `
      border-2 border-intercasa-red text-intercasa-red
      hover:bg-intercasa-red hover:text-white
      focus:ring-intercasa-red
      disabled:border-gray-400 disabled:text-gray-400
    `,
    'outline-white': `
      border-2 border-white text-white
      hover:bg-white hover:text-intercasa-dark
      focus:ring-white
      disabled:border-gray-400 disabled:text-gray-400
    `,
    ghost: `
      bg-transparent text-intercasa-dark
      hover:bg-intercasa-bone
      focus:ring-intercasa-dark
      disabled:text-gray-400
    `,
    white: `
      bg-white text-intercasa-dark
      hover:bg-intercasa-bone
      focus:ring-white
      shadow-lg
    `,
  };

  // Tamaños
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  };

  return (
    <button
      ref={buttonRef}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      <span ref={textRef} className="relative z-10">
        {children}
      </span>
      
      {/* Efecto de onda al hacer hover */}
      <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300" />
    </button>
  );
};

MagneticButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  strength: PropTypes.number,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'outline-white', 'ghost', 'white']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  disabled: PropTypes.bool,
  type: PropTypes.string,
};

export default MagneticButton;
